import { db } from './db';
import { subDays, format, startOfDay, differenceInDays, parseISO } from 'date-fns';
import { parseLocalDateString } from './dateUtils';

/**
 * 習慣の統計情報を計算するユーティリティ
 */

export interface HabitStats {
  currentStreak: number;
  bestStreak: number;
  totalCompleted: number;
}

/**
 * 特定の習慣のストリークを計算する
 */
export const calculateStreak = async (habitUuid: string): Promise<HabitStats> => {
  const records = await db.records
    .where('habitUuid')
    .equals(habitUuid)
    .and(r => r.status === 'completed')
    .sortBy('date');
  
  if (records.length === 0) {
    return { currentStreak: 0, bestStreak: 0, totalCompleted: 0 };
  }

  const sortedRecords = records.reverse(); // 新しい順
  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);
  
  let currentStreak = 0;
  
  // 今日の記録があるか確認
  const hasToday = sortedRecords.some(r => r.date === format(today, 'yyyy-MM-dd'));
  const hasYesterday = sortedRecords.some(r => r.date === format(yesterday, 'yyyy-MM-dd'));

  if (!hasToday && !hasYesterday) {
    currentStreak = 0;
  } else {
    let checkDate = hasToday ? today : yesterday;
    for (const record of sortedRecords) {
      const recordDate = parseISO(record.date);
      if (format(recordDate, 'yyyy-MM-dd') === format(checkDate, 'yyyy-MM-dd')) {
        currentStreak++;
        checkDate = subDays(checkDate, 1);
      } else if (recordDate < checkDate) {
        break;
      }
    }
  }

  // ベストストリークの計算（全期間）
  let bestStreak = 0;
  let tempStreak = 0;
  let prevDate: Date | null = null;

  // 古い順に並び替え
  const chronRecords = [...records].reverse();
  for (const record of chronRecords) {
    const recordDate = parseISO(record.date);
    if (!prevDate || differenceInDays(recordDate, prevDate) === 1) {
      tempStreak++;
    } else if (differenceInDays(recordDate, prevDate) > 1) {
      tempStreak = 1;
    }
    bestStreak = Math.max(bestStreak, tempStreak);
    prevDate = recordDate;
  }

  return {
    currentStreak,
    bestStreak,
    totalCompleted: records.length
  };
};

/**
 * ユーザー全体の統計情報を IndexedDB から一括計算する
 */
export const calculateGlobalStats = async (): Promise<any> => {
  return await db.transaction('r', [db.habits, db.records], async () => {
    const habits = await db.habits.where('isArchived').equals(0).toArray();
    // statusインデックスを使用して高速にカウント
    const totalCompleted = await db.records.where('status').equals('completed').count();
    const completedRecords = await db.records.where('status').equals('completed').toArray();
    
    // 習慣ごとのストリークを計算して、その最大値をユーザーの現在のストリークとする
    let maxCurrentStreak = 0;
    let maxBestStreak = 0;
    
    for (const habit of habits) {
      const stats = await calculateStreak(habit.uuid);
      if (stats.currentStreak > maxCurrentStreak) maxCurrentStreak = stats.currentStreak;
      if (stats.bestStreak > maxBestStreak) maxBestStreak = stats.bestStreak;
    }
    
    // 特殊条件のカウント
    let earlyBirdCount = 0;
    let nightOwlCount = 0;
    let weekendCount = 0;
    
    completedRecords.forEach(r => {
      try {
        const date = parseLocalDateString(r.date);
        const day = date.getDay();
        if (day === 0 || day === 6) weekendCount++;
        
        const timestamp = r.completedAt || r.updatedAt;
        if (timestamp) {
          const hour = new Date(timestamp).getHours();
          if (hour >= 4 && hour < 8) earlyBirdCount++;
          if (hour >= 22 || hour < 4) nightOwlCount++;
        }
      } catch (e) {
        console.warn('[Stats] Failed to parse record date:', r.date, e);
      }
    });

    return {
      totalCompleted,
      currentStreak: maxCurrentStreak,
      longestStreak: maxBestStreak,
      habitsCount: habits.length,
      earlyBirdCount,
      nightOwlCount,
      weekendCount
    };
  });
};

/**
 * 週間目標の達成状況を計算する
 */
export const calculateWeeklyProgress = async (habitUuid: string, startDate: Date): Promise<number> => {
  const endDate = addDays(startDate, 6);
  const startStr = format(startDate, 'yyyy-MM-dd');
  const endStr = format(endDate, 'yyyy-MM-dd');
  
  const count = await db.records
    .where('habitUuid')
    .equals(habitUuid)
    .and(r => r.date >= startStr && r.date <= endStr && r.status === 'completed')
    .count();
    
  return count;
};

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
